import PropTypes from "prop-types";
import { objOf, partial, pipe } from "ramda";
import React, { createRef } from "react";
import { CreditCardInputs } from "./payment/CreditCardInputs";
import Checkbox from "components/checkbox";
import Input from "components/input";
import CreditCardAddressAutocomplete from "components/online_ordering/cart/credit_card_address_autocomplete";

import { trackAnalytic, withTracking } from "helpers/analytics";
import { handleErrors } from "helpers/credit_cards";
import { getTargetChecked, getTargetValue } from "helpers/event";

class NewCreditCardForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = { copyDeliveryAddress: false };
    this.toggleCopyDelivery = this.toggleCopyDelivery.bind(this);
    this.copyDeliveryAddress = this.copyDeliveryAddress.bind(this);
    this.address2Ref = createRef();
  }

  toggleCopyDelivery(value) {
    this.setState({ copyDeliveryAddress: value });

    if (value) {
      this.copyDeliveryAddress();
    }
  }

  copyDeliveryAddress() {
    const {
      contactInfo: {
        address: { line1, line2, city, state, zip },
      },
      onNewCreditCardUpdate,
    } = this.props;

    onNewCreditCardUpdate({
      address1: line1,
      address2: line2,
      city,
      state,
      zip,
    });
  }

  render() {
    const {
      flags,
      newCreditCard,
      onBlurValidateField,
      onNewCreditCardUpdate,
      orderingOptions,
    } = this.props;
    const { copyDeliveryAddress } = this.state;
    const { firstName, lastName, address2, city, state, zip } = newCreditCard;

    let copyDeliveryAddressCheckbox;
    if (orderingOptions.selectedTransport === "Delivery") {
      copyDeliveryAddressCheckbox = (
        <div className="should-copy">
          <Checkbox
            label="Billing address is the same as delivery address"
            checked={copyDeliveryAddress}
            onChangeHandler={pipe(getTargetChecked, this.toggleCopyDelivery)}
          />
        </div>
      );
    }

    return (
      <div className="new-credit-card-form form">
        <CreditCardInputs
          newCreditCard={newCreditCard}
          onNewCreditCardUpdate={onNewCreditCardUpdate}
          onBlurValidateField={onBlurValidateField}
        />
        <div className="row">
          <label>First name</label>
          <Input
            id="new-credit-card-form-first-name"
            error={handleErrors("firstName", newCreditCard)}
            onBlur={withTracking(
              partial(onBlurValidateField, ["firstName", newCreditCard]),
              "OLO - Checkout - enter billing first name"
            )}
            autoComplete="cc-given-name"
            required
            type="text"
            value={firstName}
            onChange={pipe(
              getTargetValue,
              objOf("firstName"),
              onNewCreditCardUpdate
            )}
          />
        </div>
        <div className="row">
          <label>Last name</label>
          <Input
            id="new-credit-card-form-last-name"
            type="text"
            onBlur={withTracking(
              partial(onBlurValidateField, ["lastName", newCreditCard]),
              "OLO - Checkout - enter billing last name"
            )}
            error={handleErrors("lastName", newCreditCard)}
            value={lastName}
            autoComplete="cc-family-name"
            required
            onChange={pipe(
              getTargetValue,
              objOf("lastName"),
              onNewCreditCardUpdate
            )}
          />
        </div>
        {!flags.uoloShorterPaymentForm && (
          <>
            {copyDeliveryAddressCheckbox}
            <div className="row" key="address-c">
              <CreditCardAddressAutocomplete
                autofocusRef={this.address2Ref}
                newCreditCard={newCreditCard}
                onNewCreditCardUpdate={onNewCreditCardUpdate}
                onBlurValidateField={onBlurValidateField}
              />
            </div>
            <div className="row" key="address-2">
              <label>Address 2</label>
              <Input
                id="new-credit-card-form-address-2"
                type="text"
                error={handleErrors("address2", newCreditCard)}
                autoComplete="address-line2"
                placeholder="Apartment, suite, unit, etc"
                value={address2}
                onChange={pipe(
                  getTargetValue,
                  objOf("address2"),
                  onNewCreditCardUpdate
                )}
                onBlur={() =>
                  trackAnalytic("OLO - Checkout - enter billing address 2")
                }
                ref={this.address2Ref}
              />
            </div>
            <div className="row" key="city">
              <label>City</label>
              <Input
                id="new-credit-card-form-city"
                type="text"
                error={handleErrors("city", newCreditCard)}
                onBlur={withTracking(
                  partial(onBlurValidateField, ["city", newCreditCard]),
                  "OLO - Checkout - enter billing city"
                )}
                value={city}
                required
                onChange={pipe(
                  getTargetValue,
                  objOf("city"),
                  onNewCreditCardUpdate
                )}
              />
            </div>
            <div className="row" key="state-and-zip">
              <label>State / Province</label>
              <Input
                id="new-credit-card-form-state"
                type="text"
                error={handleErrors("state", newCreditCard)}
                value={state}
                onBlur={withTracking(
                  partial(onBlurValidateField, ["state", newCreditCard]),
                  "OLO - Checkout - enter billing state"
                )}
                maxLength="2"
                required
                onChange={pipe(
                  getTargetValue,
                  objOf("state"),
                  onNewCreditCardUpdate
                )}
              />
              <label className="zip">Zip / Postal Code</label>
              <Input
                id="new-credit-card-form-zip"
                type="text"
                value={zip.replace(/\s+/g, "")}
                error={handleErrors("zip", newCreditCard)}
                onBlur={withTracking(
                  partial(onBlurValidateField, ["zip", newCreditCard]),
                  "OLO - Checkout - enter billing zip"
                )}
                autoComplete="postal-code"
                maxLength="10"
                required
                onChange={pipe(
                  getTargetValue,
                  objOf("zip"),
                  onNewCreditCardUpdate
                )}
              />
            </div>
          </>
        )}
      </div>
    );
  }
}

NewCreditCardForm.propTypes = {
  flags: PropTypes.object,
  newCreditCard: PropTypes.shape({
    cardNumber: PropTypes.string,
    expiryMonth: PropTypes.string,
    expiryYear: PropTypes.string,
    cvv: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }).isRequired,
  onNewCreditCardUpdate: PropTypes.func.isRequired,
  orderingOptions: PropTypes.object.isRequired,
  contactInfo: PropTypes.object.isRequired,
  onBlurValidateField: PropTypes.func.isRequired,
};

export default NewCreditCardForm;
